import React from "react"
import classnames from "classnames"
import { ReactComponent as EditIcon } from "../../images/icons/edit.svg"
import Switch from "../../components/Switch/Switch"
import Input from "../../components/Input/Input"
import styles from "./index.module.scss"
import services from "../../services"
import { toast } from "react-toastify"
import ToastInfo from "../../components/ToastContents/ToastInfo"

const NotificationsSettingsContainer = ({
  data,
  updateSettings,
  refreshSettings,
}) => {
  const [email, setEmail] = React.useState(data.email)
  const [phoneNumber, setPhoneNumber] = React.useState(data.phone_number)
  const [allowNotifications, setAllowNotifications] = React.useState(
    data.email_notifications !== false,
  )
  const [sendToEmail, setSendToEmail] = React.useState(
    data.email_notifications === true,
  )
  const [sendToMobile, setSendToMobile] = React.useState(
    data.mobile_notifications !== false,
  )
  const [editingPhone, setEditingPhone] = React.useState(false)

  React.useEffect(() => {
    setEmail(data.email)
    setPhoneNumber(data.phone_number)
    setAllowNotifications(data.email_notifications !== false)
    setSendToEmail(data.email_notifications === true)
    setSendToMobile(data.mobile_notifications !== false)
  }, [data])

  const handleToggleAllowNotifications = () => {
    const newValue = !allowNotifications
    setAllowNotifications(newValue)
    if (!newValue) {
      setSendToEmail(false)
      setSendToMobile(false)
    }
    updateSettings({ email_notifications: newValue })
      .then((updatedData) => {
      })
      .catch(() => {
        setAllowNotifications(!newValue)
        showToast("Failed to update notification settings")
      })
  }

  const handleToggleSendToEmail = () => {
    const newValue = !sendToEmail
    setSendToEmail(newValue)
    updateSettings({ email_notifications: newValue })
      .then((updatedData) => {
      })
      .catch(() => {
        setSendToEmail(!newValue)
        showToast("Failed to update email notification settings")
      })
  }

  const handleToggleSendToMobile = () => {
    const newValue = !sendToMobile
    setSendToMobile(newValue)
  }

  // Email is read-only, no edit functionality

  const handleUpdatePhone = (newPhone) => {
    if (!newPhone || newPhone.trim() === "") {
      setEditingPhone(false)
      return
    }
    updateSettings({ phone_number: newPhone.trim() })
      .then((updatedData) => {
        setEditingPhone(false)
        // State will be updated from the response automatically
      })
      .catch(() => {
        setEditingPhone(false)
        showToast("Failed to update phone number")
      })
  }

  const showToast = (message) => {
    toast.info(<ToastInfo>{message}</ToastInfo>, {
      position: toast.POSITION.TOP_RIGHT,
    })
  }

  const hideDetail = ({ detail, type }) => {
    let res = null

    if (detail) {
      if (type === "email") {
        const emailArray = detail.split("@")
        const partToHide = emailArray[0].slice(1, emailArray[0].length - 2)
        res = detail.replace(partToHide, "******")
      } else if (type === "phone_number") {
        const partToHide = detail.slice(1, detail.length - 2)
        res = detail.replace(partToHide, "**********")
      }
    }

    return res && <div className={styles.settingsValue}>{res}</div>
  }

  return (
    <div className={styles.settingsWrap}>
      <h1 className={styles.settingsTitle}>
        Account Settings Notifications
      </h1>

      <div className={styles.settingsContent}>
        <div className={styles.settingsTable}>
          {/* Allow notifications */}
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Allow notifications</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                defaultActive={allowNotifications}
                action={handleToggleAllowNotifications}
                switchStyle="big"
              />
            </div>
          </div>

          {/* Email - Read-only */}
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Email</div>
            </div>
            <div className={styles.settingsTableCol}>
              {hideDetail({ detail: email, type: "email" })}
            </div>
          </div>

          {/* Send to email */}
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Send to email</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                defaultActive={sendToEmail}
                action={handleToggleSendToEmail}
                switchStyle="big"
                disabled={!allowNotifications}
              />
            </div>
          </div>

          {/* Mobile */}
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Mobile</div>
            </div>
            <div className={styles.settingsTableCol}>
              {editingPhone ? (
                <PhoneEditForm
                  initialPhone={phoneNumber}
                  onSave={handleUpdatePhone}
                  onCancel={() => setEditingPhone(false)}
                />
              ) : (
                <>
                  {phoneNumber ? (
                    <>
                      {hideDetail({ detail: phoneNumber, type: "phone_number" })}
                      <div
                        className={styles.settingsEditBtn}
                        onClick={() => setEditingPhone(true)}
                      >
                        <EditIcon />
                      </div>
                    </>
                  ) : (
                    <div 
                      className={styles.settingsValue}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setEditingPhone(true)}
                    >
                      Add +
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Send to mobile */}
          <div className={styles.settingsTableRow}>
            <div className={styles.settingsTableCol}>
              <div className={styles.settingsLabel}>Send to mobile</div>
            </div>
            <div className={styles.settingsTableCol}>
              <Switch
                defaultActive={sendToMobile}
                action={handleToggleSendToMobile}
                switchStyle="big"
                disabled={!allowNotifications}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}


const PhoneEditForm = ({ initialPhone, onSave, onCancel }) => {
  const [phone, setPhone] = React.useState(initialPhone || "")

  const handleSubmit = (e) => {
    e.preventDefault()
    const trimmedPhone = phone.trim()
    if (trimmedPhone) {
      // Allow saving even if it's the same value (for new phone numbers)
      onSave(trimmedPhone)
    } else {
      // If empty, just cancel
      onCancel()
    }
  }

  return (
    <form onSubmit={handleSubmit} className={styles.editForm}>
      <Input
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="Enter phone number"
        autoFocus
      />
      <div className={styles.editFormButtons}>
        <button type="button" onClick={onCancel} className={styles.cancelBtn}>
          Cancel
        </button>
        <button type="submit" className={styles.saveBtn}>
          Save
        </button>
      </div>
    </form>
  )
}

export default NotificationsSettingsContainer

